/**
 * Vendor wire vocabularies — plan §17.5; plans/contactout-implementation-plan.md §5.2.
 *
 * ActOS speaks ONE vocabulary: `SENIORITIES`, `SIZE_BANDS` and `INDUSTRIES`
 * (this directory). Each people-data vendor speaks its own, and the two fail
 * differently: Amplemarket rejects the whole request (HTTP 400) on one unknown
 * value; ContactOut answers HTTP 200 with zero results for an unknown industry
 * (verified 2026-09-03) and HTTP 400 for an unknown seniority or company size.
 * So the translation here is TOTAL over the canonical lists — `Record<Seniority, …>`
 * and `Record<SizeBand, …>` make the compiler refuse a canonical value with no
 * vendor answer — and a value with no equivalent comes back as `null`, never as
 * a guess. Adapters (workers/actos-api/src/connectors/*-provider.ts) call these;
 * nothing in this file knows about HTTP.
 *
 * Runtime imports: none from this package (see the barrel note in ./index.ts).
 */

import type { VerificationResult } from '../verification/index';
import type { Seniority, SizeBand } from './index';

/**
 * The people-data vendors ActOS can be pointed at, in the order the admin
 * select lists them. Both the API's port (`connectors/people-data.ts`) and the
 * UI's `useVendor` hook import this tuple, so a third vendor is declared in
 * exactly one place.
 */
export const PEOPLE_VENDORS = ['amplemarket', 'contactout'] as const;
export type PeopleVendor = (typeof PEOPLE_VENDORS)[number];

export function isPeopleVendor(value: unknown): value is PeopleVendor {
  return typeof value === 'string' && (PEOPLE_VENDORS as readonly string[]).includes(value);
}

/**
 * Per-filter option ceilings, confirmed live against each API.
 *
 *   Amplemarket: "'company_industries' exceeds the maximum options per filter (10)"
 *                — applies to `person_seniorities` too; `person_titles` is uncapped.
 *   ContactOut:  `industry`, `seniority`, `job_title` are documented `array, max:50`.
 *
 * `Record<PeopleVendor, number>` is what makes a third vendor fail to compile
 * here until it has a ceiling.
 */
export const VENDOR_FILTER_LIMITS: Record<PeopleVendor, number> = {
  amplemarket: 10,
  contactout: 50,
};

// ---- Seniority ----------------------------------------------------------

/**
 * ContactOut's accepted `seniority[]` values (accepted-values sheet; matched
 * case-insensitively by the API — `["vp"]` and `["VP"]` count the same, verified).
 */
export const CONTACTOUT_SENIORITIES = [
  'Owner / Founder',
  'CXO',
  'Partner',
  'VP',
  'Head',
  'Director',
  'Manager',
  'Senior',
  'Entry',
  'Intern',
] as const;

export type ContactOutSeniority = (typeof CONTACTOUT_SENIORITIES)[number];

/**
 * Canonical → ContactOut. Three of ours collapse onto `Owner / Founder`, one
 * renames (`C-Suite` → `CXO`), and two have NO equivalent: `Other` and
 * `Non-Manager` are Amplemarket catch-alls that ContactOut does not model.
 * `null` there is deliberate — the adapter drops the filter and SAYS so in the
 * persona note, rather than sending a value the API 400s (verified: an unknown
 * seniority fails `people/count` with a pointer at the accepted-values sheet).
 */
const CONTACTOUT_SENIORITY_BY_CANONICAL: Record<Seniority, ContactOutSeniority | null> = {
  Owner: 'Owner / Founder',
  Founder: 'Owner / Founder',
  'Founder/Owner': 'Owner / Founder',
  'C-Suite': 'CXO',
  Partner: 'Partner',
  VP: 'VP',
  Head: 'Head',
  Director: 'Director',
  Manager: 'Manager',
  Senior: 'Senior',
  Entry: 'Entry',
  Intern: 'Intern',
  Other: null,
  'Non-Manager': null,
};

/** Lowercased lookup, built once — persona text and stored run JSON are free-cased strings. */
const CONTACTOUT_SENIORITY_BY_LOWER = new Map<string, ContactOutSeniority | null>(
  (
    Object.entries(CONTACTOUT_SENIORITY_BY_CANONICAL) as [Seniority, ContactOutSeniority | null][]
  ).map(([ours, theirs]) => [ours.toLowerCase(), theirs]),
);

/**
 * The ContactOut spelling of a canonical seniority, or `null` when ContactOut
 * has no such level (`Other`, `Non-Manager`) or the input is not canonical.
 * Case-insensitive on the way in; the returned value is exactly what the wire
 * wants.
 */
export function toContactOutSeniority(canonical: string): ContactOutSeniority | null {
  return CONTACTOUT_SENIORITY_BY_LOWER.get(canonical.trim().toLowerCase()) ?? null;
}

// ---- Company size -------------------------------------------------------

/**
 * ActOS band → ContactOut `company_size[]` value. ContactOut spells the open
 * top band `10001` (no plus). The Amplemarket label form (`'51-200 employees'`)
 * is rejected with HTTP 400 (verified), which is why the canonical query never
 * carries a label.
 */
export const CONTACTOUT_SIZE_BANDS: Record<SizeBand, string> = {
  '1-10': '1_10',
  '11-50': '11_50',
  '51-200': '51_200',
  '201-500': '201_500',
  '501-1000': '501_1000',
  '1001-5000': '1001_5000',
  '5001-10000': '5001_10000',
  '10001+': '10001',
};

/** ActOS band → Amplemarket `company_sizes[]` label — today's `AMPLEMARKET_SIZES`, keyed. */
export const AMPLEMARKET_SIZE_LABELS: Record<SizeBand, string> = {
  '1-10': '1-10 employees',
  '11-50': '11-50 employees',
  '51-200': '51-200 employees',
  '201-500': '201-500 employees',
  '501-1000': '501-1000 employees',
  '1001-5000': '1001-5000 employees',
  '5001-10000': '5001-10000 employees',
  '10001+': '10001+ employees',
};

/**
 * Resolve free text to an ActOS band, or `null`.
 *
 * Tolerates what people and older rows actually contain: a trailing
 * "employees" (Amplemarket's label, and what `prospect_runs.query` stored
 * before this change), en/em dashes, thousands separators, surrounding space,
 * and ContactOut's `51_200` / `10001` forms. Exact after that — `'1'` used to
 * match four Amplemarket labels through `startsWith`, which was a bug.
 */
export function canonicalSizeBand(value: string | null | undefined): SizeBand | null {
  if (!value) return null;
  let token = value
    .trim()
    .toLowerCase()
    .replace(/[–—]/g, '-')
    .replace(/,/g, '')
    .replace(/\s*employees?\s*$/i, '')
    .replace(/\s+/g, '')
    .replace(/_/g, '-');
  if (token === '10001') token = '10001+';
  return (Object.keys(CONTACTOUT_SIZE_BANDS) as SizeBand[]).find((band) => band === token) ?? null;
}

export function toContactOutSizeBand(band: string): string | null {
  const canonical = canonicalSizeBand(band);
  return canonical ? CONTACTOUT_SIZE_BANDS[canonical] : null;
}

export function toAmplemarketSizeLabel(band: string): string | null {
  const canonical = canonicalSizeBand(band);
  return canonical ? AMPLEMARKET_SIZE_LABELS[canonical] : null;
}

// ---- Email verification -------------------------------------------------

/** `GET /v1/email/verify` and the batch job's per-address `result` values. */
export type ContactOutVerifyStatus = 'valid' | 'invalid' | 'accept_all' | 'disposable' | 'unknown';

/** Statuses ContactOut bills a verifier credit for; `disposable` and `unknown` are free. */
export const CONTACTOUT_VERIFIER_BILLED: ReadonlySet<string> = new Set([
  'valid',
  'invalid',
  'accept_all',
]);

/**
 * ContactOut's verdict onto ActOS's `VerificationResult` + catch-all flag —
 * the shape `addressConfidence` and `verdictFor` already consume.
 *
 *   valid       → deliverable
 *   invalid     → undeliverable   (absolute disproof, scores 0)
 *   accept_all  → deliverable + catchAll  (the domain answered, not the mailbox)
 *   disposable  → risky           (Amplemarket folds disposable into `risky` too)
 *   unknown / anything else → unknown
 *
 * Tolerant of case and of a null (a batch job can omit an address it could not
 * process); an unrecognised string is `unknown`, not a throw — this runs inside
 * the inject flow, where a throw would lose the reveal that was just paid for.
 */
export function contactOutVerification(
  status: string | null | undefined,
): { result: VerificationResult; catchAll: boolean } {
  switch ((status ?? '').trim().toLowerCase()) {
    case 'valid':
      return { result: 'deliverable', catchAll: false };
    case 'invalid':
      return { result: 'undeliverable', catchAll: false };
    case 'accept_all':
      return { result: 'deliverable', catchAll: true };
    case 'disposable':
      return { result: 'risky', catchAll: false };
    default:
      return { result: 'unknown', catchAll: false };
  }
}
